export type DatePreset = 'today' | 'yesterday' | '24h' | '7d' | '30d' | '90d' | 'all';

export type DateFilterValue =
  | { kind: 'preset'; preset: DatePreset }
  | { kind: 'day'; day: string }
  | { kind: 'range'; from: string; to: string };

export const PRESETS: { key: DatePreset; label: string; hours?: number }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: '90d', label: 'Last 90 days', hours: 24 * 90 },
  { key: 'all', label: 'All time' },
];

/** Today's date as YYYY-MM-DD, in local time or UTC. */
export function todayIn(utc: boolean): string {
  const now = new Date();
  if (utc) return now.toISOString().slice(0, 10);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function addDays(day: string, days: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The instant a day starts, as an ISO string. */
function startOf(day: string, utc: boolean): string {
  return new Date(`${day}T00:00:00${utc ? 'Z' : ''}`).toISOString();
}

/**
 * The filter as an instant range: `from` inclusive, `to` exclusive, either
 * left out when open. Rolling ranges count back from now.
 */
export function dateBounds(value: DateFilterValue, utc: boolean): { from?: string; to?: string } {
  if (value.kind === 'day') return { from: startOf(value.day, utc), to: startOf(addDays(value.day, 1), utc) };
  if (value.kind === 'range') return { from: startOf(value.from, utc), to: startOf(addDays(value.to, 1), utc) };
  const today = todayIn(utc);
  if (value.preset === 'today') return { from: startOf(today, utc) };
  if (value.preset === 'yesterday') return { from: startOf(addDays(today, -1), utc), to: startOf(today, utc) };
  const hours = PRESETS.find((p) => p.key === value.preset)?.hours;
  return hours ? { from: new Date(Date.now() - hours * 3600 * 1000).toISOString() } : {};
}

function formatDay(day: string, withYear: boolean): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: withYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  });
}

export function dateLabel(value: DateFilterValue, allLabel = 'All time'): string {
  if (value.kind === 'preset') return value.preset === 'all' ? allLabel : PRESETS.find((p) => p.key === value.preset)!.label;
  if (value.kind === 'day') return formatDay(value.day, true);
  const sameYear = value.from.slice(0, 4) === value.to.slice(0, 4);
  return `${formatDay(value.from, !sameYear)} – ${formatDay(value.to, true)}`;
}

/** A week back from `last`, but not before `min`: where a new custom range starts. */
export function defaultFrom(last: string, min: string | undefined): string {
  const weekBack = addDays(last, -6);
  return min && weekBack < min ? min : weekBack;
}
